import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_AdminTransactionsResponseDados } from '../contracts/Subscriptions_AdminTransactionsResponse'
import type { Subscriptions_AdminTransactionRow } from '../contracts/Subscriptions_AdminTransactionRow'
import type { Subscriptions_AdminTransactionRefundResponseDados } from '../contracts/Subscriptions_AdminTransactionRefundResponse'
// Import side-effects: registers the contracts with the responseContract registry.
import '../contracts/Subscriptions_AdminTransactionsResponse'
import '../contracts/Subscriptions_AdminTransactionRow'
import '../contracts/Subscriptions_TransactionAttemptRow'
import '../contracts/Subscriptions_AdminTransactionRefundResponse'
import '../contracts/Subscriptions_AdminTransactionRefundInput'

/**
 * useAdminTransactions — SOLE network owner for subscriptions/admin/transactions.
 *
 * All requests go to `config.api.sistemaHost` (app-api-service).
 * The `no_httpservice_in_vue` qa rule forbids any fetch in .vue files —
 * this composable is the single gateway.
 *
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const transactionRows = ref<Subscriptions_AdminTransactionRow[]>([])

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useAdminTransactions() {
  /**
   * listTransactions — GET subscriptions/admin/transactions.
   * Populates the module-scope transactionRows ref.
   */
  async function listTransactions(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('subscriptions/admin/transactions'),
        { responseContract: 'Subscriptions_AdminTransactionsResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: Subscriptions_AdminTransactionsResponseDados }
      transactionRows.value = resp.dados.rows
      log('useAdminTransactions', 'listTransactions', 'Transações carregadas', { count: transactionRows.value.length })
    } catch (err) {
      log('useAdminTransactions', 'listTransactions', 'Erro ao carregar transações', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * markRefunded — POST subscriptions/admin/transactions/{id}/refunded (S12/M3c · D5).
   *
   * Records that a human already refunded this charge in the gateway's own
   * panel. It moves no money — "cancelar ≠ estornar".
   *
   * THROWS on the envelope, not on `dados`. MY_Controller::sendError() replies
   * HTTP 200 (it never touches the status code), so httpService cannot throw on
   * a refusal by itself and `resp.success` is the only reliable signal. A
   * caller reading `dados.success` would report "estornado" for a charge the
   * backend refused to touch — §4 of skill billing-staff-console, three shipped
   * instances. Same discipline as useBillingThreeSixty.reprocessEvent().
   */
  async function markRefunded(transactionId: number): Promise<Subscriptions_AdminTransactionRefundResponseDados> {
    const raw = await httpService.post(
      sistemaUrl(`subscriptions/admin/transactions/${transactionId}/refunded`),
      {},
      {
        inputContract: 'Subscriptions_AdminTransactionRefundInput',
        responseContract: 'Subscriptions_AdminTransactionRefundResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'mark_refunded_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_AdminTransactionRefundResponseDados }
    log('useAdminTransactions', 'markRefunded', 'Transação marcada como estornada', {
      transactionId,
      status: resp.dados?.status,
    })
    return resp.dados
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    transactionRows.value = []
  }

  return {
    // reactive state
    transactionRows,
    // methods
    listTransactions,
    markRefunded,
    _reset,
  }
}
